import { useCallback, useEffect, useState } from 'react';
import { FUTURE_DATE_MESSAGE, INVALID_DATE_MESSAGE } from '../constants';
import { DateSegments, Segment } from '../types';
import { useDateValidation } from './useDateValidation';

type UseDateSegmentHandlersParams = {
  segments: DateSegments;
  setSegments: {
    setYear: (value: string) => void;
    setMonth: (value: string) => void;
    setDay: (value: string) => void;
  };
  onChange?: (value: string | undefined) => void;
  refs: {
    year: React.RefObject<HTMLInputElement>;
    month: React.RefObject<HTMLInputElement>;
    day: React.RefObject<HTMLInputElement>;
  };
  setIsEditing: (editing: boolean) => void;
  disallowFutureDates?: boolean;
  segmentOrder: [Segment, Segment, Segment];
  onValidationError?: (error: string | undefined) => void;
};

export function useDateSegmentHandlers({
  segments,
  setSegments,
  onChange,
  refs,
  setIsEditing,
  disallowFutureDates = false,
  segmentOrder,
  onValidationError,
}: UseDateSegmentHandlersParams) {
  const { year, month, day } = segments;
  const { setYear, setMonth, setDay } = setSegments;
  const { getMaxDayForMonth, validateAndFormatDate, isFutureDate } =
    useDateValidation();
  const [internalError, setInternalError] = useState<string | undefined>();

  useEffect(() => {
    if (onValidationError) onValidationError(internalError);
  }, [internalError, onValidationError]);

  // Get the next segment in order for auto-advance
  const getNextSegment = useCallback(
    (currentSegment: Segment): Segment | null => {
      const currentIndex = segmentOrder.indexOf(currentSegment);
      if (currentIndex === -1 || currentIndex === segmentOrder.length - 1) {
        return null;
      }
      return segmentOrder[currentIndex + 1];
    },
    [segmentOrder]
  );

  // Handle segment change and trigger onChange
  const onSegmentChange = useCallback(
    (newYear: string, newMonth: string, newDay: string) => {
      // Check if segments are "complete enough" to validate
      // A segment is "complete" if it has the expected number of digits
      // This prevents auto-formatting while user is still typing
      const yearComplete = newYear.length === 4;
      const monthComplete = newMonth.length === 2;
      const dayComplete = newDay.length === 2;

      // Only validate and call onChange if all segments are complete
      // This prevents "1" from being auto-formatted to "01" while user is typing "11"
      const allSegmentsComplete = yearComplete && monthComplete && dayComplete;

      const isoDate = allSegmentsComplete
        ? validateAndFormatDate(newYear, newMonth, newDay)
        : null;

      if (isoDate && onChange) {
        // Check for future date if disallowFutureDates is enabled
        if (disallowFutureDates && isFutureDate(newYear, newMonth, newDay)) {
          setInternalError(FUTURE_DATE_MESSAGE);
          // Still call onChange with the value, but show error
          onChange(isoDate);
        } else {
          onChange(isoDate);
          setInternalError(undefined);
        }
      } else if (allSegmentsComplete && newYear && newMonth && newDay) {
        // All segments filled but invalid date - provide specific error message
        const yearNum = parseInt(newYear, 10);
        const monthNum = parseInt(newMonth, 10);
        const dayNum = parseInt(newDay, 10);

        let errorMessage = INVALID_DATE_MESSAGE;

        // Provide more specific error messages
        if (newYear && newMonth) {
          const maxDay = getMaxDayForMonth(newYear, newMonth);
          if (dayNum > maxDay) {
            errorMessage = `Day must be between 1 and ${maxDay} for this month`;
          } else if (dayNum === 0) {
            errorMessage = 'Day must be between 1 and 31';
          } else if (monthNum < 1 || monthNum > 12) {
            errorMessage = 'Month must be between 1 and 12';
          } else if (yearNum < 1000 || yearNum > 9999) {
            errorMessage = 'Year must be between 1000 and 9999';
          }
        } else if (dayNum > 31 || dayNum === 0) {
          errorMessage = 'Day must be between 1 and 31';
        } else if (monthNum < 1 || monthNum > 12) {
          errorMessage = 'Month must be between 1 and 12';
        } else if (yearNum < 1000 || yearNum > 9999) {
          errorMessage = 'Year must be between 1000 and 9999';
        }

        setInternalError(errorMessage);
      } else {
        // All segments are empty - clear the value
        if (onChange && !newYear && !newMonth && !newDay) {
          onChange(undefined);
        }
        setInternalError(undefined);
      }
    },
    [
      onChange,
      validateAndFormatDate,
      getMaxDayForMonth,
      disallowFutureDates,
      isFutureDate,
    ]
  );

  const onYearChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setIsEditing(true);
      const inputValue = e.target.value;
      // Only allow digits
      const digitsOnly = inputValue.replace(/\D/g, '');
      const newYear = digitsOnly.slice(0, 4);

      // No auto-correction - just set the value and let validation handle errors
      setYear(newYear);
      onSegmentChange(newYear, month, day);

      // Auto-advance to next segment when year is complete
      if (newYear.length === 4) {
        const nextSegment = getNextSegment('year');
        if (nextSegment) {
          refs[nextSegment].current?.focus();
        }
      }
    },
    [month, day, setYear, onSegmentChange, refs, setIsEditing, getNextSegment]
  );

  const onMonthChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setIsEditing(true);
      const inputValue = e.target.value;
      // Only allow digits
      const digitsOnly = inputValue.replace(/\D/g, '');
      const newMonth = digitsOnly.slice(0, 2);

      // No auto-correction - just set the value and let validation handle errors
      setMonth(newMonth);
      onSegmentChange(year, newMonth, day);

      // Auto-advance to next segment when month is complete (2 digits)
      if (newMonth.length === 2) {
        const monthNum = parseInt(newMonth, 10);
        // Only auto-advance if it's a valid month (1-12)
        if (monthNum >= 1 && monthNum <= 12) {
          const nextSegment = getNextSegment('month');
          if (nextSegment) {
            // Use setTimeout to ensure state is updated before focusing
            setTimeout(() => {
              refs[nextSegment].current?.focus();
            }, 0);
          }
        }
      }
    },
    [year, day, setMonth, onSegmentChange, refs, setIsEditing, getNextSegment]
  );

  const onDayChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setIsEditing(true);
      const inputValue = e.target.value;
      // Only allow digits
      const digitsOnly = inputValue.replace(/\D/g, '');
      const newDay = digitsOnly.slice(0, 2);

      // No auto-correction - just set the value and let validation handle errors
      setDay(newDay);
      onSegmentChange(year, month, newDay);

      // Auto-advance to next segment when day is complete (2 digits)
      if (newDay.length === 2) {
        const dayNum = parseInt(newDay, 10);
        // Only auto-advance if it's a potentially valid day (1-31)
        if (dayNum >= 1 && dayNum <= 31) {
          const nextSegment = getNextSegment('day');
          if (nextSegment) {
            // Use setTimeout to ensure state is updated before focusing
            setTimeout(() => {
              refs[nextSegment].current?.focus();
            }, 0);
          }
        }
      }
    },
    [year, month, setDay, onSegmentChange, setIsEditing, getNextSegment, refs]
  );

  const onMonthBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      // Format "1" to "01" when month field loses focus
      // Read value directly from input to avoid stale state
      const currentValue = e.target.value;
      // Only format if it's exactly "1" (single digit), not "11" or "10"
      // Check both the input value and the current month state to avoid race conditions
      if (currentValue.length === 1 && currentValue === '1' && month === '1') {
        setMonth('01');
        onSegmentChange(year, '01', day);
      }

      // Mark editing as complete after a short delay to allow onChange to fire
      setTimeout(() => {
        setIsEditing(false);
      }, 100);
    },
    [year, day, month, setMonth, onSegmentChange, setIsEditing]
  );

  const onDayBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      // Format single digit days to "08", "04", etc. when day field loses focus
      // Read value directly from input to avoid stale state
      const currentValue = e.target.value;

      // Only format if it's a single digit (1-9), not "00", "0", or empty
      // Don't format if the value is "0" (user might be clearing it)
      if (
        currentValue.length === 1 &&
        currentValue !== '0' &&
        currentValue.trim() !== ''
      ) {
        const dayNum = parseInt(currentValue, 10);
        if (dayNum >= 1 && dayNum <= 9) {
          // Check if it's valid for the current month/year
          if (year && month) {
            const maxDay = getMaxDayForMonth(year, month);
            if (dayNum <= maxDay) {
              // Valid single digit - pad it to "08", "04", etc.
              const paddedDay = currentValue.padStart(2, '0');
              setDay(paddedDay);
              onSegmentChange(year, month, paddedDay);
            }
            // If invalid, it should have been auto-corrected already in onDayChange
          } else {
            // Don't have year/month, just pad it
            const paddedDay = currentValue.padStart(2, '0');
            setDay(paddedDay);
            onSegmentChange(year, month, paddedDay);
          }
        }
      } else if (currentValue === '' || currentValue.trim() === '') {
        // Field is empty - clear it
        setDay('');
        onSegmentChange(year, month, '');
      }

      // Mark editing as complete after a short delay to allow onChange to fire
      setTimeout(() => {
        setIsEditing(false);
      }, 100);
    },
    [year, month, day, setDay, getMaxDayForMonth, onSegmentChange, setIsEditing]
  );

  return {
    onYearChange,
    onMonthChange,
    onDayChange,
    onMonthBlur,
    onDayBlur,
    onSegmentChange,
    internalError,
  };
}
